// knowledgeDetails.pipeline.graph.* — owned by the graph page.
export default {
  title: 'Knowledge graph',
  titleWithKb: 'GraphRAG — {{name}}',
  fromDocs: 'built from {{count}} documents',
  loading: 'Loading the graph…',
  counts: '{{entities}} entities · {{relations}} relations',
  capNotice:
    'This view shows at most {{nodeCap}} entities (highest PageRank first) and the {{edgeCap}} heaviest relations between them. The graph may hold more than is drawn here.',
  encoding:
    'Node size follows degree and colour follows entity type. Hover a node for its PageRank and degree.',
  configure: 'GraphRAG settings',
  canvasLabel: 'Knowledge graph view',
  types: {
    title: 'Entity types',
    hint: 'Counts are for the entities drawn in this view.',
    untyped: 'Untyped',
    ring: 'selected / query match',
    other: 'Other types ({{types}}) · {{entities}} entities',
  },
  find: {
    label: 'Find entities in the graph',
    placeholder: 'Entity name',
    hint: 'Matches entity names among the entities loaded here; matched entities on the canvas get a ring.',
    result: '{{count}} matching entities',
  },
  flow: {
    caption:
      'Where this stage sits — chunks in the index feed GraphRAG; its output feeds entity resolution and, when switched on, retrieval',
    indexSub: '{{docs}} documents',
    chunks: 'chunks',
    method: 'method {{method}}',
    entities: 'entities',
    relations: 'relations',
    resolution: 'Entity resolution',
    resolutionSub: 'Merges entities that name the same thing',
    on: 'On',
    resolutionOff:
      'Off — turn on Entity resolution in Configuration to merge duplicate entities.',
    retrieveSub: 'Use knowledge graph',
    retrieve:
      'Used only when Use knowledge graph is on in Retrieval testing or the assistant; it adds entity and relation chunks.',
  },
  view: {
    title: 'Graph view',
    caption:
      '{{shown}} of the {{drawn}} loaded entities shown here (best connected) · edges labelled with relation and weight · click an entity to open its card',
  },
  relations: {
    title: 'Relation types',
    of: '· top {{shown}} of {{total}}',
  },
  selected: {
    title: 'Selected entity',
    empty:
      'Click an entity in the graph or in Top entities to see its relations.',
    this: 'this',
    degree: 'degree {{value}}',
    pagerank: 'page rank {{value}}',
    test: 'Test a query with the graph',
  },
  how: {
    title: 'How the GraphRAG stage builds this graph',
    intro:
      'The knowledge graph is not a separate feature; it is a stage of the same pipeline that parsed, chunked and indexed the documents. It reads the chunks in the index, asks the LLM to extract entities and relations from each, and merges the result into one graph per knowledge base.',
    step: 'Step {{n}}: {{name}}',
    extract: {
      title: 'Extract',
      body: 'Each chunk is sent to the model with the prompt for the configured method (Light or General). Entity types are open-ended, so the legend shows the types the model actually produced, with counts.',
    },
    fold: {
      title: 'Fold synonyms and prune',
      body: 'Before anything is stored, newly extracted entity and relation types are compared and synonyms are folded together. Relations whose endpoints were never extracted are dropped.',
    },
    store: {
      title: 'Store the subgraph',
      body: 'The pruned subgraph is written to the graph store. Each entity carries its type, degree and page rank; each relation carries its label and a weight, the number drawn next to every edge above.',
    },
    resolve: {
      title: 'Entity resolution',
      body: 'When switched on, entities that name the same thing are scored in pairs and merged, so one real-world thing is one node.',
    },
    retrieve: {
      title: 'Retrieve with the graph',
      body: 'Nothing above is used at query time until Use knowledge graph is switched on in Retrieval testing or the assistant. Then the query is matched against entity names, and the matched entities and their relations are added as extra chunks alongside the ranked results.',
    },
  },
  top: {
    title: 'Top entities by degree',
    hint: 'Degree is the number of relations an entity has. Select one to find it in the graph.',
  },
  tooltip: {
    type: 'Entity type',
    pagerank: 'PageRank',
    degree: 'Degree',
    weight: 'Weight',
    relation: 'Relation',
  },
  empty: {
    title: 'No knowledge graph yet',
    on: 'GraphRAG is on for this knowledge base. The graph is built while documents are parsed; parse or re-parse documents to build it.',
    off: 'GraphRAG is off for this knowledge base. Turn on "Extract knowledge graph" in Configuration, then parse documents. An LLM reads every chunk to extract entities and relations.',
    toDocuments: 'Go to Documents',
    toConfiguration: 'Open Configuration',
  },
};
